import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Play, Sparkles, CheckCircle2, Bot, Clock, AlertTriangle, Settings as SettingsIcon, Wrench, Receipt, Briefcase, Shield, BarChart3, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const CAT_ICONS = {
  "AI": Sparkles, "Scadenziario": Clock, "Operatività": Briefcase,
  "Reportistica": BarChart3, "Manutenzione": Wrench, "Fatturazione": Receipt,
  "Business Development": UsersIcon, "Qualità dati": CheckCircle2,
  "Governance": Shield, "Security": Shield,
};
const CAT_COLORS = {
  "AI": "bg-amber-50 text-amber-700 border-amber-200",
  "Scadenziario": "bg-orange-50 text-orange-700 border-orange-200",
  "Operatività": "bg-blue-50 text-blue-700 border-blue-200",
  "Reportistica": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Manutenzione": "bg-slate-50 text-slate-700 border-slate-200",
  "Fatturazione": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Business Development": "bg-purple-50 text-purple-700 border-purple-200",
  "Qualità dati": "bg-teal-50 text-teal-700 border-teal-200",
  "Governance": "bg-navy-50 text-navy-700 border-navy-200",
  "Security": "bg-red-50 text-red-700 border-red-200",
};

export default function Automazioni() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("_all");
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => { const { data } = await api.get("/automations"); setItems(data); };
  useEffect(() => { load(); }, []);

  const categories = ["_all", ...Array.from(new Set(items.map(i => i.category)))];

  const toggle = async (a, value) => {
    setBusyKey(a.key);
    try { await api.put(`/automations/${a.key}`, { enabled: value, params: a.params }); load(); }
    catch { toast.error("Permesso negato"); }
    finally { setBusyKey(null); }
  };

  const runNow = async (a) => {
    setBusyKey(a.key);
    try { const { data } = await api.post(`/automations/${a.key}/run-now`); toast.success(data.details || "Eseguita"); load(); }
    catch { toast.error("Errore esecuzione"); }
    finally { setBusyKey(null); }
  };

  const filtered = tab === "_all" ? items : items.filter(i => i.category === tab);
  const enabledCount = items.filter(i => i.enabled).length;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Automazioni</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} automazioni disponibili · <span className="text-emerald-700 font-medium">{enabledCount} attive</span></p>
        </div>
        <Card className="px-5 py-3 flex items-center gap-3">
          <Bot className="w-6 h-6 text-amber-500" />
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">AI Assistant Studio</div>
            <div className="font-heading font-semibold text-navy-900">Le automazioni AI risparmiano in media 6h/settimana</div>
          </div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {categories.map(c => <TabsTrigger key={c} value={c} className="capitalize" data-testid={`auto-tab-${c}`}>{c === "_all" ? "Tutte" : c}</TabsTrigger>)}
        </TabsList>
        <TabsContent value={tab}>
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map(a => {
              const Icon = CAT_ICONS[a.category] || SettingsIcon;
              return (
                <Card key={a.key} className={`p-5 transition-all ${a.enabled ? "border-navy-200" : "opacity-80"}`} data-testid={`auto-card-${a.key}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-md border flex items-center justify-center flex-shrink-0 ${CAT_COLORS[a.category] || "bg-slate-50 border-slate-200"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-heading font-semibold text-navy-900 text-sm">{a.name}</div>
                          <Badge variant="outline" className="text-[10px] mt-1">{a.category}</Badge>
                        </div>
                        <Switch checked={a.enabled} disabled={busyKey === a.key} onCheckedChange={(v)=>toggle(a, v)} data-testid={`auto-toggle-${a.key}`} />
                      </div>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">{a.description}</p>
                      {a.params && Object.keys(a.params).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {Object.entries(a.params).filter(([k, v]) => typeof v === "number" || typeof v === "string").slice(0,4).map(([k, v]) => (
                            <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">{k}={v}</span>
                          ))}
                        </div>
                      )}
                      {a.last_run_at && <div className="text-[10px] text-slate-400 mt-2">Ultima esecuzione: {new Date(a.last_run_at).toLocaleString("it-IT")} · {a.last_run_outcome}</div>}
                      <div className="mt-3 flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busyKey === a.key} onClick={()=>runNow(a)} data-testid={`auto-run-${a.key}`}>
                          {busyKey === a.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-3 h-3 mr-1" />Esegui ora</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
